import {
  PlayerPublic,
  postRostersByRosterIdEditNameMutation,
  postRostersByRosterIdPlayersMutation,
  postRostersByRosterIdRemovePlayerByPlayerExternalIdMutation,
  SideType,
} from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertBar,
  AutocompleteSelect,
  Button,
  Dialog,
  Icons,
  Input,
  Label,
  ListOption,
  toast,
} from "@win-reality/react-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { CreatePlayersModal } from "@/components/CreatePlayersModal";
import { SelectablePlayersList } from "@/components/SelectablePlayersList";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import UploadNewPlayersModal from "@/components/upload-new-players-modal";
import { useAppContext } from "@/hooks/use-app-context";
import { useCreateRosterWithExistingPlayer } from "@/hooks/useCreateRosterWithExistingPlayer";
import { usePlayersByExternalIds } from "@/hooks/usePlayersByExternalIds";
import { useRosterPlayers } from "@/hooks/useRosterPlayers";
import { useTeamNameOptions } from "@/hooks/useTeamNameOptions";
import { addOrgPrefix } from "@/lib/utils";
import { splitName } from "@/utils/splitName";
import { parseUploadedMembers, UploadedPlayer } from "@/utils/uploadedPlayers";

const createTeamSchema = z.object({
  rosterName: z.string().min(1, "Roster name is required"),
  teamName: z.string().min(1, "Team name is required"),
  rosterId: z.string().optional(),
});

type CreateTeamFormSchema = z.infer<typeof createTeamSchema>;

/**
 * A PlayerPublic for a roster member or a just-created player, before (or
 * without) the player search returning their record. The jersey rides along
 * as `number`, which the generated type doesn't declare.
 */
const toSyntheticPlayer = (player: UploadedPlayer) =>
  ({
    id: player.externalId,
    external_ids: [player.externalId],
    known_names: [
      player.playerName || `${player.firstName} ${player.lastName}`,
    ],
    first_name: player.firstName,
    last_name: player.lastName,
    hit_side: player.bats || "right",
    pitch_side: player.throws || "right",
    number:
      player.jerseyNumber !== undefined
        ? String(player.jerseyNumber)
        : undefined,
  }) as PlayerPublic;

type CreateTeamRosterDialogProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  mode?: "create" | "edit";
  initialRoster?: {
    id: string;
    rosterName: string;
    teamName?: string;
    playerExternalIds?: string[];
    unofficialMembers?: Array<Record<string, unknown>>;
  };
  organizationId?: string;
  onSuccess?: (roster: { id: string; name: string; teamName: string }) => void;
};

function CreateTeamRosterDialog({
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  mode = "create",
  initialRoster,
  organizationId: passedOrgId,
  onSuccess,
}: CreateTeamRosterDialogProps) {
  // Component is conditionally rendered from parent using {isOpen && <CreateTeamRosterDialog />}
  // This ensures full cleanup on close and fresh mount on open, preventing state persistence issues.

  const { orgId: rawOrgId } = useAppContext();

  // Use passed organizationId if available, otherwise fall back to user's context org.
  // Ensure the org prefix is always present, regardless of the source.
  const orgIdSource = passedOrgId || rawOrgId;
  let organizationId = orgIdSource ? addOrgPrefix(orgIdSource) : "";

  if (
    mode === "edit" &&
    !passedOrgId &&
    initialRoster?.unofficialMembers &&
    initialRoster.unofficialMembers.length > 0
  ) {
    const firstMember = initialRoster.unofficialMembers[0] as Record<
      string,
      unknown
    >;
    const rosterLeague = (firstMember.league || firstMember.org) as
      | string
      | undefined;
    if (rosterLeague) {
      organizationId = rosterLeague;
    }
  }

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const queryClient = useQueryClient();

  const setIsOpen = (open: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(open);
    } else {
      setInternalOpen(open);
    }
  };

  const form = useForm<CreateTeamFormSchema>({
    resolver: zodResolver(createTeamSchema),
    mode: "onChange",
    defaultValues: {
      teamName: initialRoster?.teamName ?? "",
      rosterName: initialRoster?.rosterName ?? "",
      rosterId: initialRoster?.id,
    },
  });

  const [teamSearchValue, setTeamSearchValue] = useState("");
  const [selectedTeamOption, setSelectedTeamOption] =
    useState<ListOption<string> | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<PlayerPublic[]>([]);
  const [pendingPlayerIds, setPendingPlayerIds] = useState<Set<string>>(
    new Set(),
  );
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreatePlayerModalOpen, setIsCreatePlayerModalOpen] = useState(false);
  const hasInitialized = useRef(false);
  const hasInitializedTeamOption = useRef(false);

  const { teamNameOptions, isLoadingTeamNames } =
    useTeamNameOptions(organizationId);

  // Fetch roster players if not provided in initialRoster.
  // Gated on `isOpen` because this dialog is mounted per-row in
  // RostersTable.columns.tsx (always in the DOM, see TODO above) — without the
  // gate, every roster on the page would fire one getTeamsSearch on mount.
  const { playerExternalIds: fetchedPlayerIds } = useRosterPlayers({
    organizationId,
    rosterId: initialRoster?.id,
    enabled:
      isOpen && mode === "edit" && !initialRoster?.playerExternalIds?.length,
  });

  const rosterPlayerIds = initialRoster?.playerExternalIds?.length
    ? initialRoster.playerExternalIds
    : fetchedPlayerIds;

  // Same reason: hold off on the per-player fan-out until the dialog opens.
  const { players: fetchedRosterPlayers } = usePlayersByExternalIds(
    isOpen && mode === "edit" ? rosterPlayerIds : undefined,
  );

  // Initialize selectedPlayers for EDIT mode with roster players (only once)
  useEffect(() => {
    if (mode === "edit" && !hasInitialized.current) {
      // If we have unofficial_members from initialRoster, use them directly (they have jersey numbers)
      if (
        initialRoster?.unofficialMembers &&
        initialRoster.unofficialMembers.length > 0
      ) {
        hasInitialized.current = true;
        setSelectedPlayers(
          parseUploadedMembers(initialRoster.unofficialMembers).map(
            toSyntheticPlayer,
          ),
        );
      } else {
        // Fallback: use fetched player data if available
        const expectedPlayerCount = rosterPlayerIds?.length ?? 0;
        const hasAllPlayersLoaded =
          expectedPlayerCount > 0 &&
          fetchedRosterPlayers.length === expectedPlayerCount;

        if (hasAllPlayersLoaded) {
          hasInitialized.current = true;
          const validPlayers = fetchedRosterPlayers.map((p) => ({
            ...p,
            external_ids: p.external_ids ?? [],
          })) as PlayerPublic[];
          setSelectedPlayers(validPlayers);
        }
      }
    }
  }, [
    mode,
    initialRoster?.unofficialMembers,
    fetchedRosterPlayers,
    rosterPlayerIds,
  ]);

  const teamName = form.watch("teamName");
  const isNewTeam = teamName.trim().length > 0 && !selectedTeamOption;

  // TODO: Replace client-side filtering with server-side search once a dedicated
  // team search endpoint is available. Once useTeamNameOptions uses an endpoint
  // that supports server-side filtering by name, this client-side filter can be removed.
  const filteredTeamOptions = teamNameOptions.filter((option) =>
    option.label.toLowerCase().includes(teamSearchValue.toLowerCase()),
  );

  // Initialize selectedTeamOption from initialRoster when options are loaded (only once)
  useEffect(() => {
    if (
      mode === "edit" &&
      initialRoster?.teamName &&
      teamNameOptions.length > 0 &&
      !hasInitializedTeamOption.current
    ) {
      hasInitializedTeamOption.current = true;
      const matchingOption = teamNameOptions.find(
        (opt) => opt.label === initialRoster.teamName,
      );
      if (matchingOption) {
        setSelectedTeamOption(matchingOption);
      } else {
        // If not found in options, set search value to show it
        setTeamSearchValue(initialRoster.teamName);
      }
    }
  }, [mode, initialRoster?.teamName, teamNameOptions]);

  useEffect(() => {
    const next = selectedTeamOption?.label ?? teamSearchValue;
    if (form.getValues("teamName") !== next) {
      form.setValue("teamName", next, { shouldValidate: true });
    }
  }, [selectedTeamOption, teamSearchValue, form]);

  // Edit mode mutation for roster name and team name
  const editRosterName = useMutation({
    ...postRostersByRosterIdEditNameMutation(),
  });

  // Mutation to add players to roster
  const addPlayers = useMutation({
    ...postRostersByRosterIdPlayersMutation(),
  });

  // Mutation to remove a player from roster
  const removePlayer = useMutation({
    ...postRostersByRosterIdRemovePlayerByPlayerExternalIdMutation(),
  });

  // Create mode with existing players (Manage Rosters context)
  const { createRoster, isLoadingRosterCreation } =
    useCreateRosterWithExistingPlayer({
      organizationId: organizationId ?? "",
      onSuccess: (roster) => {
        setIsOpen(false);
        onSuccess?.(roster);
      },
    });

  const handleSubmit = async (data: CreateTeamFormSchema) => {
    if (mode === "edit") {
      await handleEditSubmit(data);
    } else if (organizationId) {
      createRoster({
        rosterName: data.rosterName,
        parentName: data.teamName,
        selectedPlayers,
      });
    }
  };

  const handleEditSubmit = async (data: CreateTeamFormSchema) => {
    try {
      const rosterId = data.rosterId ?? "";

      // Compute player diff: added and removed
      const initialIds = new Set(rosterPlayerIds ?? []);
      const currentIds = new Set(
        selectedPlayers
          .map((p) => p.external_ids?.[0])
          .filter(Boolean) as string[],
      );

      const playersToAdd = selectedPlayers.filter(
        (p) => !initialIds.has(p.external_ids?.[0] ?? ""),
      );
      const playersToRemove = Array.from(initialIds).filter(
        (id) => !currentIds.has(id),
      );

      // Step 1: Save roster name and team name
      const editNameResponse = await editRosterName.mutateAsync({
        body: {
          name: data.rosterName,
          parentName: data.teamName,
        },
        path: { roster_id: rosterId },
      });

      // Extract external ID from response for subsequent API calls
      const rosterExternalId = editNameResponse.roster?.external_ids?.[0];

      // Step 2: Add new players if any
      if (playersToAdd.length > 0) {
        const playerInfo = playersToAdd.map((player) => {
          const { firstName, lastName } = splitName(
            player.known_names?.[0] ?? "",
          );
          // The generated PlayerPublic type doesn't declare a jersey-number
          // field, but the live API serves it as either `jersey_number`
          // (snake) or `jerseyNumber` (camel). Mirrors the read pattern in
          // upload-new-players-modal.tsx so behavior stays identical.
          const playerRecord = player as unknown as Record<string, unknown>;
          const jerseyNumber =
            (playerRecord.jersey_number as string | number | undefined) ??
            (playerRecord.jerseyNumber as string | number | undefined) ??
            (playerRecord.number as string | number | undefined);

          const finalJerseyNumber =
            jerseyNumber !== undefined
              ? parseInt(String(jerseyNumber), 10)
              : undefined;

          return {
            first_name: firstName,
            last_name: lastName,
            bats: player.hit_side as SideType,
            throws: player.pitch_side as SideType,
            external_id: player.external_ids?.[0],
            ...(finalJerseyNumber !== undefined
              ? { jersey_number: finalJerseyNumber }
              : {}),
          };
        });

        await addPlayers.mutateAsync({
          path: { roster_id: rosterId },
          body: { player_info: playerInfo },
        });
      }

      // Step 3: Remove players if any (sequentially to avoid race conditions)
      if (playersToRemove.length > 0) {
        if (!rosterExternalId) {
          throw new Error("Could not retrieve roster external ID");
        }

        for (const externalId of playersToRemove) {
          try {
            await removePlayer.mutateAsync({
              path: {
                roster_id: encodeURIComponent(rosterExternalId),
                player_external_id: encodeURIComponent(externalId),
              },
            });
          } catch (error) {
            const errorMessage =
              (error as Error & { detail?: string })?.detail ||
              (error as Error)?.message ||
              "Could not remove player.";
            toast(errorMessage, {
              variant: "error",
            });
            return;
          }
        }
      }

      // Success: invalidate queries and close dialog
      toast("Roster updated successfully!", { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["getTeamsSearch"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["getRosterPlayers"],
        exact: false,
      });
      setIsOpen(false);
      const successPayload = {
        id: data.rosterId ?? "",
        name: data.rosterName,
        teamName: data.teamName,
      };
      onSuccess?.(successPayload);
    } catch (error) {
      const message = error
        ? JSON.parse((error as unknown as Error).message).detail
        : null;
      if (message) {
        toast(message, { variant: "error" });
      }
    }
  };

  const isSubmitting =
    editRosterName.isPending ||
    addPlayers.isPending ||
    removePlayer.isPending ||
    isLoadingRosterCreation;

  const handlePlayerSelectionChange = useCallback(
    (player: PlayerPublic, checked: boolean) => {
      setSelectedPlayers((prev) => {
        return checked
          ? [...prev, player]
          : prev.filter(
              (p) => p.external_ids?.[0] !== player.external_ids?.[0],
            );
      });
    },
    [],
  );

  const handlePlayersAdded = useCallback(
    (externalIds: string[], players: Array<Record<string, unknown>>) => {
      if (externalIds.length === 0) return;

      const builtPlayers = parseUploadedMembers(players).map(toSyntheticPlayer);

      // Add synthetic players to selectedPlayers at the top (prepend)
      if (builtPlayers.length > 0) {
        setSelectedPlayers((prev) => {
          const existingIds = new Set(
            prev.flatMap((p) => p.external_ids ?? []),
          );
          const newPlayers = builtPlayers.filter(
            (p) => !existingIds.has(p.external_ids?.[0] ?? ""),
          );
          const result = [...newPlayers, ...prev];
          return result;
        });
      }

      // Keep pending IDs set for when real players appear in search
      setPendingPlayerIds(new Set(externalIds));
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return (
            key != null &&
            typeof key === "object" &&
            (key as Record<string, unknown>)._id === "getPlayersSearch"
          );
        },
      });
    },
    [queryClient],
  );

  const handlePendingPlayersHydrated = useCallback(
    (players: PlayerPublic[]) => {
      setSelectedPlayers((prev) => {
        const existingIds = new Set(prev.flatMap((p) => p.external_ids ?? []));
        const newPlayers = players.filter(
          (p) => !existingIds.has(p.external_ids?.[0] ?? ""),
        );
        return [...prev, ...newPlayers];
      });
      setPendingPlayerIds(new Set());
    },
    [],
  );

  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset search state when dialog closes
      setTeamSearchValue("");
      setSelectedTeamOption(null);
    }
  };

  const dialogTitle = mode === "edit" ? "Edit Roster" : "Create Team Roster";
  const primaryButtonLabel = mode === "edit" ? "Save" : "Done";

  return (
    <>
      <Dialog
        key={isOpen ? "open" : "closed"}
        open={isOpen}
        onOpenChange={handleClose}
        size="xl"
        title={dialogTitle}
        primaryAction={{
          label: primaryButtonLabel,
          onClick: () => form.handleSubmit(handleSubmit)(),
          isDisabled: !form.formState.isValid || isSubmitting,
          isLoading: isSubmitting,
        }}
        trigger={
          isControlled ? undefined : (
            <Button
              className="relative -left-4 text-blue"
              variant="ghost"
              size="sm"
              type="button"
            >
              <Icons.Plus className="size-4" />
              Add New Team Roster
            </Button>
          )
        }
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="rosterName"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-sm mb-2">
                    <div>
                      Roster Name
                      <span className="text-red-500">*</span>
                      <div className="text-gray-400 text-xs mt-0.5 font-light">
                        Use a roster name that represents an age group and
                        season (e.g. 4U Summer 2026)
                      </div>
                    </div>
                  </Label>

                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Roster name"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="teamName"
              render={() => (
                <FormItem>
                  <Label className="text-sm mb-2">
                    <div>
                      Team Name
                      <span className="text-red-500">*</span>
                      <div className="text-gray-400 text-xs mt-0.5 font-light">
                        Use a team name that persists over time (e.g. CSU
                        Varsity, CU - JV )
                      </div>
                    </div>
                  </Label>
                  <AutocompleteSelect
                    selectedOption={selectedTeamOption}
                    onSelectedValueChange={(value) => {
                      const option = teamNameOptions.find(
                        (opt) => opt.value === value,
                      );
                      setSelectedTeamOption(option ?? null);
                      if (!option) {
                        setTeamSearchValue("");
                      }
                    }}
                    searchValue={teamSearchValue}
                    onSearchValueChange={(searchValue) => {
                      setTeamSearchValue(searchValue);
                    }}
                    onSearchDebouncedValueChange={() => {}}
                    items={filteredTeamOptions}
                    isLoading={isLoadingTeamNames}
                    placeholder="Search teams..."
                    emptyMessage="No teams found"
                    variant="dark"
                    className="w-full"
                    popoverContentClassName="z-[110]"
                    itemRenderer={(option) => (
                      <span className="text-accent-dark-1">{option.label}</span>
                    )}
                  />
                  <FormMessage />

                  {isNewTeam && (
                    <AlertBar variant="warning">
                      Team not found. This will be added as a new team in your
                      organization.
                    </AlertBar>
                  )}
                </FormItem>
              )}
            />
          </form>
        </Form>

        {/* Player section */}
        <div className="mt-4">
          <Label className="text-sm mb-2 block">Add/Remove Players</Label>
          <div className="flex gap-2 mb-4">
            <Button
              type="button"
              size="sm"
              onClick={() => setIsCreatePlayerModalOpen(true)}
              className="flex items-center gap-1 border border-[#43B856] text-[#43B856] bg-white hover:bg-[#43B856] hover:bg-opacity-10"
            >
              <Icons.Plus className="h-4 w-4" />
              Create Players
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setIsUploadModalOpen(true)}
              className="border border-black bg-white text-black hover:bg-gray-50"
            >
              Bulk Upload
            </Button>
          </div>
          <SelectablePlayersList
            key={`${isOpen ? "open" : "closed"}`}
            organizationId={organizationId}
            enablePlayersQuery={isOpen && Boolean(organizationId)}
            selectedPlayers={selectedPlayers}
            onPlayerSelectionChange={handlePlayerSelectionChange}
            pinnedPlayers={
              selectedPlayers.length > 0 ? selectedPlayers : undefined
            }
            pendingPlayerIds={pendingPlayerIds}
            onPendingPlayersHydrated={handlePendingPlayersHydrated}
          />
        </div>
      </Dialog>

      {isUploadModalOpen && (
        <UploadNewPlayersModal
          orgId={organizationId}
          open={isUploadModalOpen}
          onOpenChange={setIsUploadModalOpen}
          // Save adds uploaded players to the roster with everyone else
          preSelectedRosterId={null}
          onSubmit={() => {
            setIsUploadModalOpen(false);
          }}
          onPlayersUploaded={handlePlayersAdded}
        />
      )}
      {isCreatePlayerModalOpen && (
        <CreatePlayersModal
          open={isCreatePlayerModalOpen}
          onOpenChange={setIsCreatePlayerModalOpen}
          orgId={organizationId}
          onPlayersCreated={handlePlayersAdded}
        />
      )}
    </>
  );
}

export { CreateTeamRosterDialog };
